import { useEffect, useState } from "react"
import { Icon } from "../../components/faicon"
import Modal from "../../components/modal"
import { usePagination } from "../../components/paginator"
import { useToast } from "../../components/toaster"
import { SupermarketData } from "../../data/api.models"
import api from "../../data/api.service"
import { Product } from "../../data/models"
import { useAsyncEffect } from "../../hooks/async-effect.hook"
import { useForm, validate } from "../../hooks/form.hook"
import { usePartialState } from "../../hooks/partial-state.hook"
import { mask } from "../../utilities/mask.utils"

export default function Products() {
	const [is, setState] = usePartialState({
		loading: true,
		removing: undefined as Product | undefined
	})
	const [products, setProducts] = useState<Product[]>([])
	useAsyncEffect(async () => {
		if (!is.loading) return
		const { succeeded, data } = await api.products.all()
		if (!succeeded) return
		setProducts(data!)
		setState({ loading: false })
	}, [is.loading])

	const [PageSizer, Paginator, slice] = usePagination(products.length, 'productos')

	const [modal, setModal] = usePartialState({ product: undefined as Product | true | undefined })
	const toast = useToast()
	async function remove(product: Product) {
		setState({ removing: product })
		const { succeeded } = await api.products.remove(product.id)
		setState({ removing: undefined })
		if (!succeeded) return
		toast('success', 'Producto eliminado.')
		setState({ loading: true })
	}

	return <section className="p-6">
		<h1 className="mb-6">Productos</h1>

		<PageSizer/>
		<table className="table">
			<thead>
				<tr>
					<th>Nombre</th>
					<th className="w-0"></th>
				</tr>
			</thead>
			<tbody>
				{products.slice(slice.start, slice.end).map(product =>
				<tr key={product.id}>
					<td>{product.name}</td>
					<td>
						<div className="flex gap-2">
							<button title="Editar..." onClick={() => setModal({ product })} className="button">
								<Icon icon="pen-to-square"/>
							</button>
							<button title="Eliminar..." onClick={() => remove(product)} disabled={!!is.removing} className="button hover:bg-red-500 hover:text-white">
								{is.removing !== product
									? <Icon icon="trash-alt"/>
									: <Icon icon='spinner' pulse/>}
							</button>
						</div>
					</td>
				</tr>)}
			</tbody>
		</table>
		<Paginator/>

		<button onClick={() => setModal({ product: true })} className="button">
			<Icon icon="plus"/> Agregar producto
		</button>

		{modal.product && <Modal onDismiss={() => setModal({ product: undefined })}>
			<CreateEditProduct product={modal.product} onFinish={() => {
				setModal({ product: undefined })
				setState({ loading: true })
			}}/>
		</Modal>}
	</section>
}

function CreateEditProduct({ product: product_, onFinish }: { product: Product | true, onFinish?: () => void }) {
	const [is, setState] = usePartialState({ working: false })
	const product = useForm<{ name: string }>({
		validation: {
			name: validate.required,
		},
		validate_native: true
	})

	useEffect(() => {
		if (product_ !== true)
			product.setData({ name: product_.name })
	}, [])

	const toast = useToast()
	async function create(data: Partial<SupermarketData>) {
		setState({ working: true })
		const { succeeded, data: id } = await api.products.create(data.name!)
		setState({ working: false })
		if (!succeeded) return
		toast('success', 'Producto creado.')
		onFinish?.()
	}

	async function edit(data: typeof product.data) {
		setState({ working: true })
		const { succeeded } = await api.products.edit((product_ as Product).id, data.name!)
		setState({ working: false })
		if (!succeeded) return
		toast('success', 'Producto actualizado.')
		onFinish?.()
	}

	return <form onSubmit={product.submit(product_ === true? create : edit)}>
		<h2>{product_ === true? 'Nuevo' : 'Editar'} producto</h2>

		<div className="my-6 space-y-3">
			<div>
				<label htmlFor="name" className="font-medium">Nombre</label><br/>
				<input id="name" {...product.field('name')} className="input w-full" />
			</div>
		</div>

		<div className="flex justify-end">
			<button type="submit" disabled={is.working} className="button blue">
				{ !is.working
					? <>
						<Icon icon="save"/>
						{ product_ === true? 'Crear' : 'Guardar' }
					</> : <>
						<Icon icon='spinner' pulse/>
						{ product_ === true? 'Creando...' : 'Guardando...' }
					</> }
			</button>
		</div>
	</form>
}